import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useRegion } from '@/context/RegionContext';
import { MapPin } from 'lucide-react';

const RegionPicker = () => {
  const { showRegionPicker, selectRegion, closeRegionPicker, regions, regionKey } = useRegion();
  const { pathname } = useLocation();

  // Don't show region picker on admin pages
  if (pathname.startsWith('/admin')) return null;

  return (
    <AnimatePresence>
      {showRegionPicker && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          data-testid="region-picker-overlay"
          onClick={closeRegionPicker}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-card border border-border rounded-2xl p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="region-picker-modal"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent/10 mb-3">
                <MapPin size={22} className="text-accent" />
              </div>
              <h2 className="font-serif text-2xl font-light">Where are you shopping from?</h2>
              <p className="text-sm text-muted-foreground mt-1.5">
                This helps us show the right sizes, pricing, and shipping options
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {Object.entries(regions).map(([key, r]) => (
                <button
                  key={key}
                  onClick={() => selectRegion(key)}
                  data-testid={`region-option-${key}`}
                  className={`flex flex-col items-center p-5 rounded-xl border-2 transition-all hover:shadow-md ${
                    regionKey === key
                      ? 'border-accent bg-accent/5 shadow-sm'
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <span className="text-3xl mb-2">{r.flag}</span>
                  <span className="font-serif text-lg font-light">{r.label}</span>
                  <span className="text-[11px] text-muted-foreground mt-1">{r.currencySymbol} {r.currency}</span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">{r.delivery}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RegionPicker;
